import {
  CANCEL_KINDS, CENTER_EVENT_KINDS, movableReason, movedSlot, plannedSlots, resizeProblem, SESSION_DURATIONS, type CancelKind, type CenterEvent, type CenterEventKind,
  type CenterProgram, type SessionStatus, type TherapySession,
} from '@selfon/domain/browser';
import { useEffect, useState } from 'react';
import { api, type ClientRow, type StaffRow } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { Alert, Chip, Dialog, Field } from '../../components/ui';
import { dateTime, todayKst } from '../../format';
import { CANCEL_LABEL, dateOf, dayLabel, hm, moveProblem, sameSpot, statusText, type MoveTo } from './cal';

const VOUCHER_PROGRAMS = ['발달재활서비스', '교육청지원사업-특수교육', '지역사회서비스(아동청소년 심리지원)', '실비보험'];
const PROGRAM_SUGGESTIONS = ['언어치료', '놀이치료', '감각통합', '작업치료', '인지학습치료', '미술치료', '음악치료', '사회성 그룹', '언어평가'];

export interface SchedulePrefill { date?: string; startTime?: string; therapistId?: string }

/** 일정 등록(반복 가능). 빈 칸을 눌러 열면 날짜·시간·선생님이 채워진다. 겹치면 서버가 한 건도 만들지 않는다. */
export function ScheduleDialog({ open, prefill, clients, staff, onClose, onCreated }: {
  open: boolean; prefill: SchedulePrefill; clients: ClientRow[]; staff: StaffRow[]; onClose: () => void; onCreated: (s: TherapySession[]) => void;
}) {
  const { me } = useAuth();
  const centerId = me!.centerId!;
  const isTherapist = me!.role === 'THERAPIST';
  const teachers = staff.filter((s) => s.active);
  const [f, setF] = useState({ clientId: '', therapistId: '', date: todayKst(), startTime: '15:00', durationMin: 50, program: '', voucherProgram: '', repeatWeeks: 1 });
  const [programs, setPrograms] = useState<CenterProgram[] | null>(null);
  useEffect(() => {
    if (!open) return;
    let live = true;
    api.programs(centerId).then((r) => { if (live) setPrograms(r.programs.filter((x) => x.active)); }).catch(() => { if (live) setPrograms([]); });
    return () => { live = false; };
  }, [open, centerId]);
  // 프로그램 관리에 등록한 프로그램을 먼저 보여 준다(고른 선생님이 맡는 것 우선). 없으면 예시 이름.
  const mine = (programs ?? []).filter((x) => !x.teacherIds.length || x.teacherIds.includes(f.therapistId));
  const programChoices = programs?.length ? [...new Set([...mine, ...programs].map((x) => x.name))] : PROGRAM_SUGGESTIONS;

  useEffect(() => {
    if (!open) return;
    const therapistId = isTherapist ? me!.userId : prefill.therapistId ?? '';
    // 선생님이 정해졌으면 그 선생님 담당 아동을 먼저 고른다
    const c = clients.find((x) => therapistId && x.therapistIds?.includes(therapistId)) ?? clients[0];
    setF((x) => ({
      ...x, clientId: c?.id ?? '', date: prefill.date ?? todayKst(), startTime: prefill.startTime ?? '15:00',
      therapistId: therapistId || c?.therapistIds?.[0] || teachers[0]?.userId || '', program: '', repeatWeeks: 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  let preview: string | null = null;
  try { preview = plannedSlots({ ...f, voucherProgram: f.voucherProgram || undefined }).map((s) => dateTime(s.startAt)).join(' · '); }
  catch (e) { preview = e instanceof Error ? e.message : null; }

  return (
    <Dialog wide title="일정 등록" open={open} onClose={onClose} submitLabel={f.repeatWeeks > 1 ? `${f.repeatWeeks}건 등록` : '등록'}
      onSubmit={async () => { const r = await api.createSessions(centerId, { ...f, voucherProgram: f.voucherProgram || undefined }); onCreated(r.sessions); }}>
      {clients.length === 0 && <Alert kind="info">먼저 이용자를 등록하세요.</Alert>}
      <div className="form-grid">
        <Field id="sc-client" label="이용자">
          <select id="sc-client" required value={f.clientId} onChange={(e) => {
            const c = clients.find((x) => x.id === e.target.value);
            setF({ ...f, clientId: e.target.value, therapistId: isTherapist ? f.therapistId : (c?.therapistIds?.[0] ?? f.therapistId) });
          }}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
          </select>
        </Field>
        <Field id="sc-teacher" label="담당 선생님">
          <select id="sc-teacher" required value={f.therapistId} disabled={isTherapist} onChange={(e) => setF({ ...f, therapistId: e.target.value })}>
            {teachers.map((s) => <option key={s.userId} value={s.userId}>{s.name} ({s.title})</option>)}
          </select>
        </Field>
        <Field id="sc-date" label="첫 수업 날짜"><input id="sc-date" type="date" max="9999-12-31" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field id="sc-time" label="시작 시간"><input id="sc-time" type="time" required min="07:00" max="21:30" step={600} value={f.startTime} onChange={(e) => setF({ ...f, startTime: e.target.value })} /></Field>
        <Field id="sc-dur" label="수업 시간">
          <select id="sc-dur" value={f.durationMin} onChange={(e) => setF({ ...f, durationMin: Number(e.target.value) })}>
            {SESSION_DURATIONS.map((m) => <option key={m} value={m}>{m}분</option>)}
          </select>
        </Field>
        <Field id="sc-repeat" label="반복">
          <select id="sc-repeat" value={f.repeatWeeks} onChange={(e) => setF({ ...f, repeatWeeks: Number(e.target.value) })}>
            <option value={1}>한 번만</option>
            {[2, 3, 4, 5, 6, 8, 10, 12].map((n) => <option key={n} value={n}>매주 · {n}주</option>)}
          </select>
        </Field>
        <Field id="sc-program" label="프로그램">
          <input id="sc-program" required maxLength={40} list="program-list" value={f.program} onChange={(e) => setF({ ...f, program: e.target.value })} placeholder="예: 언어치료" />
          <datalist id="program-list">{programChoices.map((p) => <option key={p} value={p} />)}</datalist>
          {programs && !programs.length && <span className="hint">원장님이 [프로그램 관리]에 등록하면 여기서 고를 수 있습니다.</span>}
        </Field>
        <Field id="sc-voucher" label="바우처 사업">
          <select id="sc-voucher" value={f.voucherProgram} onChange={(e) => setF({ ...f, voucherProgram: e.target.value })}>
            <option value="">비바우처(일반 수납)</option>
            {VOUCHER_PROGRAMS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
      </div>
      {preview && <p className="preview-line"><strong>등록될 일정</strong> {preview}</p>}
    </Dialog>
  );
}

const STATUS_CHOICES: Array<{ status: SessionStatus; label: string }> = [
  { status: 'SCHEDULED', label: '예정' }, { status: 'COMPLETED', label: '완료(출석)' }, { status: 'NO_SHOW', label: '결석' }, { status: 'CANCELLED', label: '취소' },
];

/** 일정 상세 + 출결 변경. 결제된 회기는 사유가 필요하고 '결제 후 수정'으로 표시된다. */
export function SessionDialog({ session, clientName, staff, canWrite, canMove, onClose, onChanged, onMove, onResize }: {
  session: TherapySession | null; clientName: string; staff?: StaffRow; canWrite: boolean; canMove: boolean; onClose: () => void; onChanged: (msg: string) => void;
  onMove: (s: TherapySession) => void; onResize: (s: TherapySession) => void;
}) {
  const { me } = useAuth();
  const [status, setStatus] = useState<SessionStatus>('SCHEDULED');
  const [cancelKind, setCancelKind] = useState<CancelKind>('PLAIN');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (!session) return;
    setStatus(session.status); // 열기만 하고 저장해도 상태가 바뀌지 않게 현재 상태에서 시작한다
    setCancelKind(session.cancelKind ?? 'PLAIN');
    setReason('');
  }, [session]);
  if (!session) return null; // 닫힌 빈 대화상자를 DOM 에 남기지 않는다
  const unchanged = status === session.status && (status !== 'CANCELLED' || cancelKind === session.cancelKind);
  const reasonRequired = Boolean(session.voucherPaidAt) || session.status === 'COMPLETED';

  return (
    <Dialog wide title={`${clientName} · ${dayLabel(session.startAt.slice(0, 10))} ${hm(session.startAt)}–${hm(session.endAt)}`} open onClose={onClose}
      submitLabel={canWrite ? '출결 저장' : '닫기'}
      onSubmit={async () => {
        if (!canWrite || unchanged) return;
        const r = await api.setAttendance(me!.centerId!, session.id, { status, expectedVersion: session.version, reason: reason || undefined, cancelKind: status === 'CANCELLED' ? cancelKind : undefined });
        onChanged(`${clientName} ${dayLabel(r.session.startAt.slice(0, 10))} ${hm(r.session.startAt)} 회기를 '${statusText(r.session)}'(으)로 바꿨습니다.`);
      }}>
      <dl className="facts facts-2">
        <div><dt>프로그램</dt><dd>{session.program}</dd></div>
        <div><dt>담당</dt><dd>{staff ? `${staff.name} ${staff.title}` : '-'}</dd></div>
        <div><dt>바우처</dt><dd>{session.voucherProgram ?? '비바우처'}{session.voucherPaidAt ? ' · 결제됨' : ''}</dd></div>
        <div><dt>현재 상태</dt><dd>{statusText(session)}{session.editedAfterPayment && <> <Chip tone="warn">결제 후 수정</Chip></>}</dd></div>
        {session.movedFrom && <div><dt>처음 일정</dt><dd>{dayLabel(dateOf(session.movedFrom))} {hm(session.movedFrom)} <Chip tone="info">옮김</Chip></dd></div>}
      </dl>
      {canWrite && (
        <div className="move-row">
          <button type="button" className="btn" disabled={!canMove} onClick={() => onMove(session)}>일정 옮기기…</button>
          <button type="button" className="btn" disabled={!canMove} onClick={() => onResize(session)}>시간 조절…</button>
          {!canMove && <span className="hint">{movableReason(session) ?? '자기 일정만 옮길 수 있습니다.'}</span>}
        </div>
      )}
      {canWrite ? <>
        {session.voucherPaidAt && <Alert kind="info">바우처 결제가 끝난 회기입니다. 바꾸면 '결제 후 수정'으로 표시됩니다.</Alert>}
        <fieldset className="radios radios-row">
          <legend>출결</legend>
          {STATUS_CHOICES.map((c) => <label key={c.status}><input type="radio" name="att" checked={status === c.status} onChange={() => setStatus(c.status)} /> {c.label}</label>)}
        </fieldset>
        {status === 'CANCELLED' && (
          <fieldset className="radios radios-row">
            <legend>취소 구분</legend>
            {CANCEL_KINDS.map((k) => <label key={k}><input type="radio" name="ck" checked={cancelKind === k} onChange={() => setCancelKind(k)} /> {CANCEL_LABEL[k]}</label>)}
          </fieldset>
        )}
        <Field id="att-reason" label={reasonRequired && !unchanged ? '변경 사유 (필수)' : '변경 사유 (선택)'}>
          <input id="att-reason" required={reasonRequired && !unchanged} maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {unchanged && <p className="hint">바꿀 출결을 고르면 저장할 수 있습니다.</p>}
      </> : <p className="hint">출결 변경 권한이 없습니다.</p>}
    </Dialog>
  );
}

export interface ResizeRequest { session: TherapySession; start: string; end: string }

/** 수업 시간 조절 확인·직접 입력. 달력에서 위·아래 끝을 끌어 놓으면 그 시간으로 채워져 열린다. */
export function ResizeDialog({ req, all, clientName, onClose, onSaved }: {
  req: ResizeRequest | null; all: readonly TherapySession[]; clientName: string; onClose: () => void; onSaved: (msg: string) => void;
}) {
  const { me } = useAuth();
  const [t, setT] = useState({ start: '', end: '' });
  useEffect(() => { if (req) setT({ start: req.start, end: req.end }); }, [req]);
  if (!req) return null;
  const s = req.session;
  const problem = t.start && t.end ? resizeProblem(s, t.start, t.end, all, new Date().toISOString()) : null;
  const minutes = (() => { const [a, b] = [t.start, t.end].map((x) => Number(x.slice(0, 2)) * 60 + Number(x.slice(3))); return Number.isFinite(a) && Number.isFinite(b) ? b! - a! : 0; })();
  return (
    <Dialog title={`수업 시간 조절 · ${clientName}`} open onClose={onClose} submitLabel="변경 저장"
      onSubmit={async () => {
        if (problem) throw new Error(problem);
        const r = await api.resizeSession(me!.centerId!, s.id, { startTime: t.start, endTime: t.end, expectedVersion: s.version });
        onSaved(`${clientName} ${dayLabel(dateOf(r.session.startAt))} 수업을 ${hm(r.session.startAt)}–${hm(r.session.endAt)}(으)로 바꿨습니다.`);
      }}>
      <p className="move-line"><span className="move-from">{hm(s.startAt)}–{hm(s.endAt)}</span> → <strong>{t.start}–{t.end}</strong>{minutes > 0 && <span className="muted small"> ({minutes}분)</span>}</p>
      <div className="form-grid">
        <Field id="rs-start" label="시작 시간"><input id="rs-start" type="time" step={600} min="07:00" max="21:50" required value={t.start} onChange={(e) => setT({ ...t, start: e.target.value })} /></Field>
        <Field id="rs-end" label="종료 시간"><input id="rs-end" type="time" step={600} min="07:10" max="22:00" required value={t.end} onChange={(e) => setT({ ...t, end: e.target.value })} /></Field>
      </div>
      {problem && <Alert>{problem}</Alert>}
      <p className="hint">10분 단위, 10~240분</p>
    </Dialog>
  );
}

/**
 * 일정 옮기기. 달력에서 끌어 놓으면 놓은 자리로 채워져 확인 창으로 열리고,
 * 상세 창의 "옮기기"로 열면 날짜·시간·선생님을 직접 고른다(키보드·휴대폰용).
 */
export function MoveDialog({ session, initial, all, events, staff, clientName, onClose, onMoved }: {
  session: TherapySession | null; initial: MoveTo | null; all: readonly TherapySession[]; events: readonly CenterEvent[]; staff: StaffRow[];
  clientName: string; onClose: () => void; onMoved: (msg: string, s: TherapySession) => void;
}) {
  const { me } = useAuth();
  const isTherapist = me!.role === 'THERAPIST';
  const [to, setTo] = useState<MoveTo>({ date: '', startTime: '', therapistId: '' });
  useEffect(() => { if (session) setTo(initial ?? { date: dateOf(session.startAt), startTime: hm(session.startAt), therapistId: session.therapistId }); }, [session, initial]);
  if (!session) return null; // 닫힌 빈 대화상자를 DOM 에 남기지 않는다
  const teachers = staff.filter((s) => s.active);
  const name = (id: string) => staff.find((s) => s.userId === id)?.name ?? '선생님';
  const ready = Boolean(to.date && to.startTime && to.therapistId);
  const problem = ready ? moveProblem(session, to, all, me!, new Date().toISOString()) : null;
  const same = ready && sameSpot(session, to);
  const off = events.find((e) => e.date === to.date && e.kind === 'STAFF_OFF' && e.staffId === to.therapistId && !e.cancelled);
  let endTime = '';
  try { if (ready) endTime = hm(movedSlot(session, to).endAt); } catch { /* problem 이 이미 알려 준다 */ }

  return (
    <Dialog wide title={`일정 옮기기 · ${clientName}`} open onClose={onClose} submitLabel="옮기기"
      onSubmit={async () => {
        if (same) throw new Error('지금 일정과 같은 자리입니다. 날짜·시간·선생님 중 하나를 바꾸세요.');
        if (problem) throw new Error(problem);
        const r = await api.moveSession(me!.centerId!, session.id, { ...to, expectedVersion: session.version });
        const who = r.session.therapistId !== session.therapistId ? ` · ${name(r.session.therapistId)} 선생님` : '';
        onMoved(`${clientName} 일정을 ${dayLabel(dateOf(r.session.startAt))} ${hm(r.session.startAt)}${who}(으)로 옮겼습니다.`, r.session);
      }}>
      <p className="move-line">
        <span className="move-from">{dayLabel(dateOf(session.startAt))} {hm(session.startAt)}–{hm(session.endAt)} · {name(session.therapistId)}</span>
        <span aria-hidden="true"> → </span><span className="sr-only">에서</span>
        <strong>{ready ? `${dayLabel(to.date)} ${to.startTime}${endTime ? `–${endTime}` : ''} · ${name(to.therapistId)}` : '-'}</strong>
      </p>
      <div className="form-grid">
        <Field id="mv-date" label="날짜"><input id="mv-date" type="date" max="9999-12-31" required value={to.date} onChange={(e) => setTo({ ...to, date: e.target.value })} /></Field>
        <Field id="mv-time" label="시작 시간"><input id="mv-time" type="time" required min="07:00" max="21:30" step={600} value={to.startTime} onChange={(e) => setTo({ ...to, startTime: e.target.value })} /></Field>
        <Field id="mv-teacher" label="선생님">
          <select id="mv-teacher" value={to.therapistId} disabled={isTherapist} onChange={(e) => setTo({ ...to, therapistId: e.target.value })}>
            {teachers.map((s) => <option key={s.userId} value={s.userId}>{s.name} ({s.title})</option>)}
          </select>
        </Field>
      </div>
      {problem && <Alert>{problem}</Alert>}
      {!problem && off && <Alert kind="info">{name(to.therapistId)} 선생님은 이날 '{off.title}' 일정이 있습니다. 그래도 옮길 수 있지만 확인하세요.</Alert>}
      
    </Dialog>
  );
}

const KIND_LABEL: Record<CenterEventKind, string> = { STAFF_OFF: '휴무·결근', MEETING: '회의·워크숍', CENTER: '센터 행사' };
export const eventKindLabel = (k: CenterEventKind) => KIND_LABEL[k];

/** 종일 일정 추가(휴무·결근·회의). 치료사는 자기 휴무·결근만. */
export function AllDayDialog({ date, staff, onClose, onSaved }: { date: string | null; staff: StaffRow[]; onClose: () => void; onSaved: (msg: string) => void }) {
  const { me } = useAuth();
  const isTherapist = me!.role === 'THERAPIST';
  const [f, setF] = useState({ title: '', kind: 'STAFF_OFF' as CenterEventKind, staffId: '' });
  useEffect(() => { if (date) setF({ title: '', kind: 'STAFF_OFF', staffId: isTherapist ? me!.userId : staff.find((s) => s.active)?.userId ?? '' }); }, [date, isTherapist, me, staff]);
  return (
    <Dialog title={`종일 일정 추가 · ${date ? dayLabel(date) : ''}`} open={Boolean(date)} onClose={onClose} submitLabel="추가"
      onSubmit={async () => {
        await api.createEvent(me!.centerId!, { date: date!, title: f.title, kind: f.kind, staffId: f.kind === 'STAFF_OFF' ? f.staffId : undefined });
        onSaved(`${dayLabel(date!)}에 '${f.title}' 일정을 추가했습니다.`);
      }}>
      <fieldset className="radios radios-row">
        <legend>구분</legend>
        {CENTER_EVENT_KINDS.filter((k) => !isTherapist || k === 'STAFF_OFF').map((k) => <label key={k}><input type="radio" name="ek" checked={f.kind === k} onChange={() => setF({ ...f, kind: k })} /> {KIND_LABEL[k]}</label>)}
      </fieldset>
      {f.kind === 'STAFF_OFF' && (
        <Field id="ev-staff" label="선생님">
          <select id="ev-staff" value={f.staffId} disabled={isTherapist} onChange={(e) => setF({ ...f, staffId: e.target.value })}>
            {staff.filter((s) => s.active).map((s) => <option key={s.userId} value={s.userId}>{s.name} ({s.title})</option>)}
          </select>
        </Field>
      )}
      <Field id="ev-title" label="일정 이름"><input id="ev-title" required maxLength={40} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={f.kind === 'STAFF_OFF' ? '예: 연차, 출근X · 개인일정' : '예: 전체 사례회의'} /></Field>
    </Dialog>
  );
}

/** 종일 일정 보기·취소 */
export function EventDetailDialog({ event, staffName, canCancel, onClose, onCancelled }: {
  event: CenterEvent | null; staffName?: string; canCancel: boolean; onClose: () => void; onCancelled: (msg: string) => void;
}) {
  const { me } = useAuth();
  return (
    <Dialog danger title={event?.title ?? ''} open={Boolean(event)} onClose={onClose} submitLabel={canCancel ? '일정 취소' : '닫기'}
      onSubmit={async () => {
        if (!canCancel || !event) return;
        await api.cancelEvent(me!.centerId!, event.id, event.version);
        onCancelled(`'${event.title}' 일정을 취소했습니다.`);
      }}>
      {event && <p>{dayLabel(event.date)} · {KIND_LABEL[event.kind]}{staffName ? ` · ${staffName}` : ''}</p>}
      {!canCancel && <p className="hint">이 일정을 취소할 권한이 없습니다.</p>}
    </Dialog>
  );
}

